import { useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import { useUIStore } from '@/stores/uiStore'
import { useAuthStore } from '@/stores/authStore'
import { useIdentity } from '@/hooks/api'
import { canAccessWidgetHub } from '@/hooks/api/usePermissions'
import { Sidebar } from './Sidebar'
import { Header } from './Header'
import { MobileDrawer } from './MobileDrawer'
import { WhatsAppHubBanner } from './WhatsAppHubBanner'
import { cn } from '@/lib/utils'

interface AppLayoutProps {
  children?: React.ReactNode
}

/**
 * Main application layout with sidebar, header, and content area.
 * Responsive: shows mobile drawer on small screens, persistent sidebar on large screens.
 */
export function AppLayout({ children }: AppLayoutProps) {
  const sidebarState = useUIStore((state) => state.sidebarState)
  const initTheme = useUIStore((state) => state.initTheme)
  const bannerDismissed = useUIStore((state) => state.whatsappHubBannerDismissed)
  const dismissBanner = useUIStore((state) => state.dismissWhatsAppHubBanner)
  const user = useAuthStore((state) => state.user)

  // Fetch user identity on mount - this populates the auth store with full user data
  const { user: identityUser } = useIdentity()

  // Initialize theme on mount
  useEffect(() => {
    initTheme()
  }, [initTheme])

  const isCollapsed = sidebarState === 'collapsed'

  // The banner links into the hub, so only show it to roles that can open it.
  const customerId = identityUser?.customer_id ?? user?.customerId
  const partnerId = identityUser?.partner_id ?? user?.partnerId
  const showBanner =
    !bannerDismissed &&
    canAccessWidgetHub({
      role: user?.role || 'demo',
      customerId: customerId ? Number(customerId) : undefined,
      partnerId: partnerId ? Number(partnerId) : undefined,
    })

  return (
    <div
      className="flex min-h-screen flex-col bg-[var(--color-content-bg)] dark:bg-gray-950"
      style={{ '--app-banner-height': showBanner ? '2.5rem' : '0px' } as React.CSSProperties}
    >
      {showBanner && <WhatsAppHubBanner onDismiss={dismissBanner} />}

      <div className="flex min-w-0 flex-1">
        {/* Desktop sidebar - hidden on mobile */}
        <div
          className={cn(
            'fixed left-0 z-30 hidden transition-all duration-300 lg:block',
            showBanner ? 'top-10 bottom-0' : 'inset-y-0',
            isCollapsed ? 'w-[72px]' : 'w-[240px]'
          )}
        >
          <Sidebar />
        </div>

        {/* Mobile drawer */}
        <MobileDrawer />

        {/*
          Main content area. Overflow is clipped rather than hidden: `hidden` makes
          these scroll containers, which would anchor any `sticky` page content to
          a box that never scrolls instead of to the viewport.
        */}
        <div
          className={cn(
            'flex min-w-0 flex-1 flex-col overflow-x-clip transition-all duration-300',
            'lg:ml-[240px]',
            isCollapsed && 'lg:ml-[72px]'
          )}
        >
          {/* Mobile header - sits below the banner when it is shown */}
          <Header className={showBanner ? 'top-10' : undefined} />

          {/* Page content */}
          <main className="min-w-0 flex-1 overflow-clip p-4 lg:p-6">{children ?? <Outlet />}</main>
        </div>
      </div>
    </div>
  )
}

export default AppLayout
